import React, { useEffect, useContext, useState } from 'react';
import Axios from 'axios';
import { useNavigate } from "react-router-dom";
import { Grid, Typography, Button, TextField, Snackbar, Alert, Box, useMediaQuery, useTheme } from "@mui/material";
import { useImmerReducer } from 'use-immer';
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useTranslation } from 'react-i18next';

import DispatchContext from "../../Contexts/DispatchContext";
import StateContext from "../../Contexts/StateContext";

function Login() {
    const { t, i18n } = useTranslation();
    const { config, environment } = require('../../index');
    const navigate = useNavigate();
    const GlobalDispatch = useContext(DispatchContext);
    const GlobalState = useContext(StateContext);
    const { executeRecaptcha } = useGoogleReCaptcha();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const initialState = {
        username: '',
        password: '',
        sendRequest: 0,
        recaptchaToken: '',
        openSnack: false,
        disabledLoginBtn: false,
        serverError: false,
        recaptchaError: false,
        tooManyAttempts: false,
        twoFARequired: false,
        twoFAUserId: null,
        twoFAToken: ''
    };

    function reducer(draft, action) {
        switch (action.type) {
            case 'setUsername': draft.username = action.value; draft.serverError = false; break;
            case 'setPassword': draft.password = action.value; draft.serverError = false; break;
            case 'incrementSendRequest': draft.sendRequest++; break;
            case 'setRecaptchaToken': draft.recaptchaToken = action.value; break;
            case 'disableLoginBtn': draft.disabledLoginBtn = true; break;
            case 'enableLoginBtn': draft.disabledLoginBtn = false; break;
            case 'setServerError': draft.serverError = true; break;
            case 'setRecaptchaError': draft.recaptchaError = true; break;
            case 'setTooManyAttempts': draft.tooManyAttempts = true; break;
            case 'openSnack': draft.openSnack = true; break;
            case 'require2FA': draft.twoFARequired = true; draft.twoFAUserId = action.userId; break;
            case 'set2FAToken': draft.twoFAToken = action.value; break;
            default: break;
        }
    }

    const [state, dispatch] = useImmerReducer(reducer, initialState);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!executeRecaptcha) return;
        dispatch({ type: 'disableLoginBtn' });

        try {
            const token = await executeRecaptcha("login");
            dispatch({ type: 'setRecaptchaToken', value: token });
            dispatch({ type: 'incrementSendRequest' });
        } catch {
            dispatch({ type: 'setRecaptchaError' });
            dispatch({ type: 'enableLoginBtn' });
        }
    };

    useEffect(() => {
        if (!state.sendRequest || !state.recaptchaToken) return;

        const source = Axios.CancelToken.source();

        const login = async () => {
            try {
                const response = await Axios.post(
                    config[environment].loginEndpoint,
                    {
                        username: state.username,
                        password: state.password,
                        recaptcha: state.recaptchaToken
                    },
                    { cancelToken: source.token }
                );

                if (response.data?.["2fa_required"]) {
                    dispatch({ type: 'require2FA', userId: response.data.user_id });
                    dispatch({ type: 'enableLoginBtn' });
                    return;
                }

                if (response.data.access && response.data.refresh) {
                    localStorage.setItem("jwtAccessToken", response.data.access);
                    localStorage.setItem("jwtRefreshToken", response.data.refresh);
                    localStorage.setItem("token", response.data.access); // Backwards compatibility
                    GlobalDispatch({
                        type: 'catchToken',
                        tokenValue: response.data.access,
                        refreshToken: response.data.refresh
                    });

                    // fetch user info immediately
                    const resUser = await Axios.get(
                        config[environment].myUserEndpoint,
                        { headers: { Authorization: `Bearer ${response.data.access}` } }
                    );

                    GlobalDispatch({
                        type: "userSignsIn",
                        usernameInfo: resUser.data.username,
                        emailInfo: resUser.data.email,
                        IdInfo: resUser.data.id
                    });

                    dispatch({ type: 'openSnack' });

                }

            } catch (err) {
                dispatch({ type: 'enableLoginBtn' });
                if (err.response?.status === 429) dispatch({ type: 'setTooManyAttempts' });
                else if (err.response?.status === 403) dispatch({ type: 'setRecaptchaError' });
                else dispatch({ type: 'setServerError' });
            }
        };

        login();
        return () => source.cancel();
    }, [state.sendRequest, state.recaptchaToken, GlobalDispatch, config, environment]);

    // Redirect after login + user info fetched
    useEffect(() => {
        if (GlobalState.userIsLogged && GlobalState.userId && GlobalState.userUsername) {
            navigate(`/${i18n.language}`);
        }
    }, [GlobalState.userIsLogged, GlobalState.userId, GlobalState.userUsername, navigate, i18n.language]);

    // 2FA submit
    const handle2FASubmit = async () => {
        try {
            const response = await Axios.post(
                config[environment].twoFALoginEndpoint,
                { user_id: state.twoFAUserId, token: state.twoFAToken }
            );

            localStorage.setItem("jwtAccessToken", response.data.access);
            localStorage.setItem("jwtRefreshToken", response.data.refresh);
            localStorage.setItem("token", response.data.access); // Backwards compatibility
            GlobalDispatch({
                type: 'catchToken',
                tokenValue: response.data.access,
                refreshToken: response.data.refresh
            });

            const userRes = await Axios.get(
                config[environment].myUserEndpoint,
                { headers: { Authorization: `Bearer ${response.data.access}` } }
            );

            GlobalDispatch({
                type: "userSignsIn",
                usernameInfo: userRes.data.username,
                emailInfo: userRes.data.email,
                IdInfo: userRes.data.id
            });

        } catch {
            dispatch({ type: 'setServerError' });
        }
    };


    return (
        <Box sx={{ 
            width: { xs: '90%', sm: '70%', md: '50%', lg: '40%' }, 
            margin: { xs: "2rem auto", md: "3rem auto" },
            marginTop: { xs: '20px', md: '50px', lg: '100px' },
            border: "2px solid black", 
            padding: { xs: '1.5rem', md: '3rem' } 
        }}>
            {!state.twoFARequired ? (
                <form onSubmit={handleSubmit}>
                    <Grid container justifyContent="center">
                        <Typography variant={isMobile ? 'h5' : 'h4'}>{t('Administration.LoginTitle')}</Typography>
                    </Grid>

                    {state.serverError && <Alert severity="error">{t('Administration.InvalidLogin')}</Alert>}
                    {state.recaptchaError && <Alert severity="warning">{t('Administration.RecaptchaFailed')}</Alert>}
                    {state.tooManyAttempts && <Alert severity="error">{t('Administration.TooManyAttempts')}</Alert>}

                    <Grid container sx={{ marginTop: "1rem" }}>
                        <TextField
                            label={t('Administration.Username')}
                            variant="outlined"
                            fullWidth
                            value={state.username}
                            onChange={e => dispatch({ type: 'setUsername', value: e.target.value })}
                            error={state.serverError}
                        />
                    </Grid>

                    <Grid container sx={{ marginTop: "1rem" }}>
                        <TextField
                            label={t('Administration.Password')}
                            type="password"
                            variant="outlined"
                            fullWidth
                            value={state.password}
                            onChange={e => dispatch({ type: 'setPassword', value: e.target.value })}
                            error={state.serverError}
                        />
                    </Grid>

                    <Grid container sx={{ marginTop: "1rem" }}>
                        <Button
                            variant='contained'
                            fullWidth
                            type='submit'
                            disabled={state.disabledLoginBtn}
                        >
                            {t('Administration.LoginButton')}
                        </Button>
                    </Grid>
                </form>
            ) : (
                <div>
                    <Grid container justifyContent="center">
                        <Typography variant='h4'>{t('Administration.TwoFactorTitle')}</Typography>
                    </Grid>

                    {state.serverError && <Alert severity="error">{t('Administration.InvalidOtp')}</Alert>}

                    <Grid container sx={{ marginTop: "1rem" }}>
                        <TextField
                            label={t('Administration.OtpToken')}
                            variant="outlined"
                            fullWidth
                            value={state.twoFAToken}
                            onChange={e => dispatch({ type: 'set2FAToken', value: e.target.value })}
                            error={state.serverError}
                        />
                    </Grid>

                    <Grid container sx={{ marginTop: "1rem" }}>
                        <Button variant='contained' fullWidth onClick={handle2FASubmit}>
                            {t('Administration.VerifyButton')}
                        </Button>
                    </Grid>
                </div>
            )}

            {/* --- Password reset link --- */}
            <Grid item container justifyContent="center" sx={{ marginTop: "1rem" }}>
                <Typography variant="body2">
                    {t('Administration.ForgotPassword')}{" "}
                    <span
                        onClick={() => navigate(`/${i18n.language}/password-reset`)}
                        style={{ cursor: "pointer", color: "green" }}
                    >
                        {t('Administration.ResetPassword')}
                    </span>
                </Typography>
            </Grid>

            {/* --- Sign up link --- */}
            <Grid item container justifyContent="center" sx={{ marginTop: "1rem" }}>
                <Typography variant='small'>
                    {t('Administration.NoAccount')}{" "}
                    <span
                        onClick={() => navigate(`/${i18n.language}/register`)}
                        style={{ cursor: "pointer", color: "green" }}
                    >
                        {t('Administration.SignUpNow')}
                    </span>
                </Typography>
            </Grid>

            <Snackbar
                open={state.openSnack}
                message={t('Administration.LoginSuccess')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </Box>
    );
}

export default Login;